import React, { useRef, useState, useEffect, useCallback } from 'react';

/**
 * LazyPdfThumbnail — Renders a PDF page thumbnail ONLY when it enters the viewport.
 *
 * WHY THIS EXISTS:
 * The original code rendered N separate <PdfViewer> components for N pages all at once
 * in the sidebar. For a 100-page PDF this meant 100 simultaneous PDF instances,
 * each loading and decoding the full document — causing the browser to freeze.
 *
 * This component uses IntersectionObserver to detect when a thumbnail enters the
 * visible scroll area. Until then it renders a lightweight placeholder div of the
 * same size. This means at most ~5-8 thumbnails are ever rendering simultaneously.
 *
 * HOW IT WORKS:
 * 1. A placeholder <div> is always rendered (maintains scroll height)
 * 2. IntersectionObserver watches it; when it enters the viewport, `isVisible` → true
 * 3. Only then does it render the canvas and call getPage() to draw the thumbnail
 * 4. Once rendered, the canvas stays rendered (no re-render on scroll out)
 *
 * @param {object}   props
 * @param {Function} props.getPage       - From usePdfDocument: (pageNumber) => Promise<PDFPageProxy>
 * @param {number}   props.pageNumber    - 1-indexed page number
 * @param {number}   props.scale         - Canvas render scale (e.g. 0.3 for sidewall)
 * @param {number}   props.placeholderW  - Width of placeholder before render (px)
 * @param {number}   props.placeholderH  - Height of placeholder before render (px)
 */
const LazyPdfThumbnail = ({
  getPage,
  pageNumber,
  scale = 0.3,
  placeholderW = 120,
  placeholderH = 160,
}) => {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [isVisible, setIsVisible] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [renderError, setRenderError] = useState(false);

  // --- IntersectionObserver: trigger render when scrolled into view ---
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // One-shot: once visible, never need to check again
        }
      },
      {
        root: null,       // Relative to the browser viewport
        rootMargin: '100px', // Pre-load slightly before entering view (smooth UX)
        threshold: 0,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // --- Canvas rendering: only runs when IntersectionObserver fires ---
  useEffect(() => {
    if (!isVisible || isRendered || !canvasRef.current) return;

    let cancelled = false;

    const render = async () => {
      try {
        const page = await getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const ctx = canvas.getContext('2d');

        // Cancel any in-flight render task (e.g., if component re-renders)
        if (renderTaskRef.current) {
          await renderTaskRef.current.cancel();
        }

        const renderTask = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = renderTask;

        // Yield to the browser event loop before starting canvas draw.
        // This prevents a burst of simultaneous canvas operations from blocking UI.
        await new Promise((resolve) => requestAnimationFrame(resolve));

        await renderTask.promise;

        if (!cancelled) {
          setIsRendered(true);
        }
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.warn(`[LazyPdfThumbnail] Page ${pageNumber} render failed:`, err);
          if (!cancelled) setRenderError(true);
        }
      }
    };

    render();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel?.();
    };
  }, [isVisible, isRendered, getPage, pageNumber, scale]);

  return (
    <div
      ref={wrapperRef}
      style={{
        width: placeholderW,
        minHeight: placeholderH,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Placeholder shown until the canvas is ready */}
      {!isRendered && !renderError && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: renderError ? '#fdd' : '#e8ecf0',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            color: '#999',
          }}
        >
          {isVisible ? '...' : ''}
        </div>
      )}

      {/* {renderError && (
        <div style={{ fontSize: 10, color: '#c00', padding: 4, textAlign: 'center' }}>
          ⚠ Page {pageNumber}
        </div>
      )} */}

      {/* Canvas: always in the DOM once visible (avoids re-render flicker) */}
      <canvas
        ref={canvasRef}
        style={{
          display: isRendered ? 'block' : 'none',
          maxWidth: '100%',
          height: 'auto',
        }}
      />
    </div>
  );
};

export default LazyPdfThumbnail;
